import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../components/Layout';
import PageHeader from '../components/PageHeader';
import { ExternalLink, Plus, Search } from 'lucide-react';
import axiosInstance from '../utils/axiosInstance';
import { priceOrigin, searchKey } from '../lib/utils';
import IngredientPhoto from '../components/IngredientPhoto';
import '../styles/garde-manger.css';

/* ---------- helpers ---------- */
const fmtEur = (v) => `${Number(v ?? 0).toFixed(2).replace('.', ',')} €`;
const UNIT_LABEL = { kg: 'kg', g: 'g', l: 'litre', cl: 'cl', ml: 'mL', unit: 'pièce' };
const unitToType = (u) =>
  (['kg', 'g'].includes(u) ? 'weight' : ['l', 'cl', 'ml'].includes(u) ? 'volume' : 'quantity');
const uomToUnit = (uom) => {
  const u = (uom || '').toLowerCase();
  if (u.includes('kg')) return 'kg';
  if (u === 'g' || u.includes('gram')) return 'g';
  if (u.includes('cl')) return 'cl';
  if (u.includes('ml')) return 'ml';
  if (u === 'l' || u.includes('lit')) return 'l';
  return null;
};

const EMOJI_PALETTE = ['🧈', '🍫', '🌾', '🥚', '🥛', '🍬', '🌰', '🍋', '🍓', '🫐', '🍎', '🍐', '🍯', '🧂', '🧀', '🍵', '🥥', '🥕', '🍌', '☕', '🥄'];
const EMOJI_RULES = [
  [/beurre/i, '🧈'], [/choco|cacao/i, '🍫'], [/farine|blé|epeautre|épeautre/i, '🌾'],
  [/oeuf|œuf/i, '🥚'], [/lait|crème|creme/i, '🥛'], [/sucre|panela/i, '🍬'],
  [/amande|noisette|noix|pécan|pecan|pistache/i, '🌰'], [/citron|yuzu/i, '🍋'],
  [/fraise/i, '🍓'], [/myrtille|cassis/i, '🫐'], [/pomme/i, '🍎'], [/poire/i, '🍐'],
  [/banan/i, '🍌'], [/miel/i, '🍯'], [/sel/i, '🧂'], [/feta|parmesan|comté|comte|fromage|maquée|maquee|raclette/i, '🧀'],
  [/matcha|thé|the |hojicha|sencha|genmaicha/i, '🍵'], [/coco/i, '🥥'],
  [/carotte/i, '🥕'], [/café|cafe|espresso/i, '☕'], [/abricot/i, '🍑'],
  [/datte|rhubarbe|fruit/i, '🍂'], [/chia|graine|sésame|sesame/i, '🌱'],
];
const guessEmoji = (name) => (EMOJI_RULES.find(([re]) => re.test(name || '')) || [, '🥄'])[1];

const relativeFr = (dateStr) => {
  if (!dateStr) return null;
  const d = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
  if (d <= 0) return "aujourd'hui";
  if (d < 7) return `il y a ${d} j`;
  if (d < 31) return `il y a ${Math.max(1, Math.round(d / 7))} sem.`;
  return `il y a ${Math.max(1, Math.round(d / 30))} mois`;
};
const freshness = (ing) => {
  if (!Number(ing.unit_price)) return { cls: 'gray', label: 'Sans prix' };
  const dateStr = ing.price_date || ing.price_updated_at || ing.last_invoice_date;
  const origin = priceOrigin(ing.price_source, dateStr);
  if (ing.price_source === 'estimate' || /estimation/i.test(ing.name || '')) return { cls: 'gray', label: origin };
  if (!dateStr) return { cls: 'gray', label: origin };
  const days = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
  return days <= 21 ? { cls: 'green', label: origin } : { cls: 'amber', label: `${origin}, à vérifier` };
};

// Fetch the source invoice PDF through our backend (Odoo access not required) and open it.
const openInvoicePdf = async (odooId) => {
  try {
    const res = await axiosInstance.get(`invoice-pdf/?odoo_id=${odooId}`, { responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) { console.error('open invoice pdf failed', e); alert('Facture PDF introuvable.'); }
};

/* The product photo when there is one, otherwise the emoji. */
const IngredientIcon = ({ ingredient }) => (
  <IngredientPhoto className="gm-ico" imageUrl={ingredient.image_url} emoji={ingredient.emoji || guessEmoji(ingredient.name)} />
);

/* ---------- add-ingredient sheet ---------- */
const AddSheet = ({ onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [emoji, setEmoji] = useState('🥄');
  const [touchedEmoji, setTouchedEmoji] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [chosen, setChosen] = useState(null); // {price, unit, source_product, supplier, source}
  const [manual, setManual] = useState(false);
  const [unit, setUnit] = useState('kg');
  const [saving, setSaving] = useState(false);

  // auto-suggest emoji from the name until the user picks one
  useEffect(() => { if (!touchedEmoji) setEmoji(guessEmoji(name)); }, [name, touchedEmoji]);
  // seed the price search with the name
  useEffect(() => { if (name && !chosen && !manual) setQ(name); }, [name]); // eslint-disable-line

  useEffect(() => {
    if (chosen || manual || q.trim().length < 2) { setResults([]); return; }
    let active = true;
    const t = setTimeout(async () => {
      try {
        const { data } = await axiosInstance.get(`ingredients/price-lookup/?q=${encodeURIComponent(q.trim())}`);
        if (active) setResults(Array.isArray(data) ? data : []);
      } catch { /* ignore */ }
    }, 220);
    return () => { active = false; clearTimeout(t); };
  }, [q, chosen, manual]);

  const pick = (p) => {
    setChosen({ price: p.unit_price, qty: (p.quantity_per_unit && Number(p.quantity_per_unit) > 1 ? String(p.quantity_per_unit) : '1'),
      unit: p.unit, source_product: p.id, supplier: p.supplier_name,
      source: 'invoice', invoiceId: p.last_invoice_odoo_id, invoiceNumber: p.last_invoice_number });
    setUnit(p.unit || 'kg');
  };
  const price = chosen ? (parseFloat(chosen.price) / (parseFloat(chosen.qty) || 1)) : null;
  const unitChips = useMemo(() => {
    const base = ['kg', 'g', 'l', 'unit'];
    if (chosen && !base.includes(chosen.unit)) base.splice(3, 0, chosen.unit);
    return base;
  }, [chosen]);

  const canSave = name.trim() && chosen && chosen.price !== '' && chosen.price != null && !isNaN(parseFloat(chosen.price)) && !saving;

  const submit = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const { data: ing } = await axiosInstance.post('ingredients/', {
        name: name.trim(), emoji, unit, type: unitToType(unit),
        source_product: chosen.source_product ?? null,
      });
      await axiosInstance.post(`ingredients/${ing.id}/set-price/`, {
        pack_price: chosen.price, pack_quantity: chosen.qty || 1, unit, source: chosen.source || 'manual',
      });
      onCreated();
    } catch (e) {
      console.error('create ingredient failed', e);
      setSaving(false);
    }
  };

  return (
    <div className="gm-overlay" onClick={onClose}>
      <div className="gm-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="gm-sheet-h"><h3>Nouvel ingrédient</h3><button className="gm-x" onClick={onClose}>✕</button></div>

        <div className="gm-step">
          <div className="lbl"><span className="num">1</span>Comment ça s’appelle&nbsp;?</div>
          <input className="gm-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Beurre" autoFocus />
          <div className="gm-emoji-row">
            {[emoji, ...EMOJI_PALETTE.filter((e) => e !== emoji)].slice(0, 12).map((e) => (
              <button key={e} className={`gm-emoji ${e === emoji ? 'on' : ''}`}
                onClick={() => { setEmoji(e); setTouchedEmoji(true); }}>{e}</button>
            ))}
          </div>
        </div>

        <div className="gm-step">
          <div className="lbl"><span className="num">2</span>Combien ça coûte&nbsp;?</div>
          {chosen && chosen.source === 'invoice' ? (
            <div className="gm-chosen">
              <div className="gm-chosen-head">
                <span>✓ {chosen.supplier || 'depuis vos factures'}</span>
                <button type="button" className="gm-relink-btn" onClick={() => { setChosen(null); setManual(false); }}>changer</button>
              </div>
              {chosen.invoiceId ? (
                <button type="button" className="gm-invoice-link gm-confirm-invoice" onClick={() => openInvoicePdf(chosen.invoiceId)}>
                  📄 voir la facture{chosen.invoiceNumber ? ` ${chosen.invoiceNumber}` : ''} pour vérifier
                </button>
              ) : null}
              <div className="gm-pe-conv" style={{ marginTop: 10 }}>
                <input className="gm-input" type="number" step="0.01" value={chosen.price}
                  onChange={(e) => setChosen({ ...chosen, price: e.target.value, source: 'invoice' })} />
                <span className="gm-pe-x">€ pour</span>
                <input className="gm-input gm-pe-qty" type="number" step="0.01" value={chosen.qty}
                  onChange={(e) => setChosen({ ...chosen, qty: e.target.value, source: 'invoice' })} />
              </div>
              {price != null && !isNaN(price) && <div className="gm-pe-result">= {fmtEur(price)} / {unit}</div>}
              <div className="gm-pe-cap" style={{ marginTop: 8, marginBottom: 0 }}>Ex. la facture montre 31 € pour 18 kg → 18 ici</div>
            </div>
          ) : manual ? (
            <input className="gm-input" type="number" step="0.01" placeholder="Prix en €" autoFocus
              value={chosen?.source === 'manual' ? chosen.price : ''}
              onChange={(e) => setChosen({ price: e.target.value, qty: '1', unit, source: 'manual' })} />
          ) : (
            <>
              <input className="gm-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Chercher dans vos factures (ex. beurre)…" />
              <div className="gm-lookup">
                <div className="hint">{results.length > 0 ? 'Prix trouvés dans vos factures' : 'Cherchez un produit pour reprendre son prix'}</div>
                {results.map((p) => (
                  <button key={p.id} className="gm-res" onClick={() => pick(p)}>
                    <span style={{ fontSize: 18 }}>{guessEmoji(p.name)}</span>
                    <span className="nm">{p.name.length > 52 ? p.name.slice(0, 52) + '…' : p.name}
                      <span className="rsub">{p.supplier_name || '—'}{p.last_invoice_date ? ` · ${new Date(p.last_invoice_date).toLocaleDateString('fr-FR')}` : ''}</span></span>
                    <span className="leader" /><span className="rp">{fmtEur(p.unit_price)}/{p.unit}</span>
                  </button>
                ))}
                {q.trim().length >= 2 && results.length === 0 && (
                  <div className="gm-lookup-empty">Rien trouvé. Saisissez le prix vous-même ci-dessous.</div>
                )}
                <button className="gm-res" onClick={() => { setManual(true); setChosen(null); }}>
                  <span style={{ fontSize: 18 }}>✏️</span>
                  <span className="nm">Saisir un prix moi-même<span className="rsub">si ce n’est pas dans la liste</span></span>
                  <span className="leader" /><span className="rp" style={{ color: 'var(--faint)' }}>€ —</span>
                </button>
              </div>
            </>
          )}
        </div>

        <div className="gm-step">
          <div className="lbl"><span className="num">3</span>Vous l’utilisez en…</div>
          <div className="gm-units">
            {unitChips.map((u) => (
              <button key={u} className={`gm-chip ${u === unit ? 'on' : ''}`} onClick={() => setUnit(u)}>{UNIT_LABEL[u] || u}</button>
            ))}
          </div>
        </div>

        {canSave && <div className="gm-preview">{name.trim()} : {fmtEur(price)}/{unit}, prêt pour vos plats</div>}
        <button className="gm-cta" disabled={!canSave} onClick={submit}>{saving ? 'Un instant…' : 'Ajouter aux ingrédients'}</button>
      </div>
    </div>
  );
};

/* ---------- ingredient detail (history + drift + archive) ---------- */
export const DetailSheet = ({ ingredient, onClose, onChanged }) => {
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [relinking, setRelinking] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [manualMode, setManualMode] = useState(false);
  const [priceInput, setPriceInput] = useState('');
  const [qtyInput, setQtyInput] = useState('1');
  const [mUnit, setMUnit] = useState(ingredient.unit);
  const [editingPrice, setEditingPrice] = useState(false);
  const [pendingProduct, setPendingProduct] = useState(null);
  const [recentOpen, setRecentOpen] = useState(false);
  const [recentLines, setRecentLines] = useState(null);
  const ing = ingredient;

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axiosInstance.get(`ingredients/${ing.id}/history/`);
        setHistory(Array.isArray(data) ? data : []);
      } catch { /* ignore */ }
    })();
  }, [ing.id]);

  // "Check for updates": pull recent invoices for the reference product (review only).
  const loadRecent = async () => {
    setRecentOpen(true); setRecentLines(null);
    try {
      const { data } = await axiosInstance.get(`ingredients/${ing.id}/recent-prices/`);
      setRecentLines(Array.isArray(data) ? data : []);
    } catch { setRecentLines([]); }
  };
  // Use a recent invoice line as the reference to set the price (conversion prefilled).
  const useRecentLine = (ln) => {
    setRecentOpen(false);
    setEditingPrice(true);
    setPriceInput(ln.price_subtotal != null ? String(ln.price_subtotal) : String(ln.price_unit || ''));
    setQtyInput(ln.quantity ? String(ln.quantity) : '1');
    setMUnit(uomToUnit(ln.uom) || ing.unit);
  };
  const setActive = async (is_active) => {
    setBusy(true);
    try { await axiosInstance.patch(`ingredients/${ing.id}/`, { is_active }); onChanged(); }
    catch { setBusy(false); }
  };
  const toggleRts = async () => {
    setBusy(true);
    try { await axiosInstance.patch(`ingredients/${ing.id}/`, { ready_to_sell: !ing.ready_to_sell }); onChanged(); }
    catch { setBusy(false); }
  };

  // Search the catalog to re-assign which invoice product this ingredient is linked to.
  useEffect(() => {
    if (!relinking || q.trim().length < 2) { setResults([]); return; }
    let active = true;
    const t = setTimeout(async () => {
      try {
        const { data } = await axiosInstance.get(`ingredients/price-lookup/?q=${encodeURIComponent(q.trim())}`);
        if (active) setResults(Array.isArray(data) ? data : []);
      } catch { /* ignore */ }
    }, 220);
    return () => { active = false; clearTimeout(t); };
  }, [q, relinking]);

  // Link the product as reference AND set the price/unit you reviewed in the card.
  const relinkTo = async (p) => {
    if (priceInput === '' || isNaN(parseFloat(priceInput))) return;
    setBusy(true);
    try {
      await axiosInstance.post(`ingredients/${ing.id}/link-product/`, { source_product: p.id });
      await axiosInstance.post(`ingredients/${ing.id}/set-price/`, {
        pack_price: priceInput, pack_quantity: qtyInput || 1, unit: mUnit, source: 'invoice',
      });
      onChanged();
    } catch { setBusy(false); }
  };

  // Set the price by hand, with no catalog product link.
  const saveManual = async () => {
    const price = parseFloat(priceInput);
    if (isNaN(price)) return;
    setBusy(true);
    try {
      await axiosInstance.patch(`ingredients/${ing.id}/`, { source_product: null });
      await axiosInstance.post(`ingredients/${ing.id}/set-price/`, { price, unit: mUnit, source: 'manual' });
      onChanged();
    } catch { setBusy(false); }
  };

  // Edit the price by hand as a conversion (X € for Y units), keeping the catalog link.
  const savePriceEdit = async () => {
    if (priceInput === '' || isNaN(parseFloat(priceInput))) return;
    setBusy(true);
    try {
      await axiosInstance.post(`ingredients/${ing.id}/set-price/`, {
        pack_price: priceInput, pack_quantity: qtyInput || 1, unit: mUnit, source: 'manual',
      });
      onChanged();
    } catch { setBusy(false); }
  };
  const peResult = (() => {
    const pp = parseFloat(priceInput), pq = parseFloat(qtyInput) || 1;
    return isNaN(pp) ? null : pp / pq;
  })();

  const pts = [...history].reverse().map((h) => Number(h.price));
  const spark = (() => {
    if (pts.length < 2) return null;
    const min = Math.min(...pts), max = Math.max(...pts), span = max - min || 1;
    const step = 146 / (pts.length - 1);
    return pts.map((p, i) => `${(i * step).toFixed(0)},${(44 - ((p - min) / span) * 34).toFixed(0)}`).join(' ');
  })();

  return (
    <div className="gm-overlay" onClick={onClose}>
      <div className="gm-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="gm-detail-h">
          <IngredientIcon ingredient={ing} />
          <div className="dt"><h3>{ing.name}</h3>
            <div className="price gm-mono">{fmtEur(ing.unit_price)} <span style={{ color: 'var(--ink-soft)' }}>/{UNIT_LABEL[ing.unit] || ing.unit}</span>
              {!editingPrice && (
                <button className="gm-price-edit-btn" onClick={() => {
                  setEditingPrice(true); setMUnit(ing.unit);
                  setPriceInput(ing.pack_price != null ? String(ing.pack_price) : (ing.unit_price ? String(ing.unit_price) : ''));
                  setQtyInput(ing.pack_quantity && Number(ing.pack_quantity) !== 1 ? String(ing.pack_quantity) : '1');
                }}>Modifier</button>
              )}
            </div>
            {!editingPrice && ing.pack_price != null && Number(ing.pack_quantity) !== 1 && (
              <div className="gm-breakdown gm-mono">{fmtEur(ing.pack_price)} ÷ {Number(ing.pack_quantity)} {ing.unit} = {fmtEur(ing.unit_price)}/{ing.unit}</div>
            )}
          </div>
          <button className="gm-x" onClick={onClose}>✕</button>
        </div>

        {!editingPrice && (
          <div className="gm-source gm-price-origin">
            <b>{priceOrigin(ing.price_source, ing.price_date || ing.price_updated_at)}</b>
            {ing.price_note ? <>. {ing.price_note}</> : null}
            {ing.webshop_url && (
              <a className="gm-webshop-link" href={ing.webshop_url} target="_blank" rel="noopener noreferrer">
                <ExternalLink size={14} /> Voir sur le webshop
              </a>
            )}
          </div>
        )}

        {editingPrice && (
          <div className="gm-price-editor">
            <div className="gm-pe-cap">Prix &amp; conversion</div>
            <div className="gm-pe-conv">
              <input className="gm-input" type="number" step="0.01" autoFocus placeholder="Prix payé" value={priceInput} onChange={(e) => setPriceInput(e.target.value)} />
              <span className="gm-pe-x">€ pour</span>
              <input className="gm-input gm-pe-qty" type="number" step="0.01" placeholder="1" value={qtyInput} onChange={(e) => setQtyInput(e.target.value)} />
            </div>
            <div className="gm-units" style={{ marginTop: 8 }}>
              {['kg', 'g', 'l', 'cl', 'ml', 'unit'].map((u) => (
                <button key={u} className={`gm-chip ${u === mUnit ? 'on' : ''}`} onClick={() => setMUnit(u)}>{UNIT_LABEL[u] || u}</button>
              ))}
            </div>
            {peResult != null && <div className="gm-pe-result">= {fmtEur(peResult)} / {mUnit}</div>}
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <button className="gm-btn-ghost" onClick={() => setEditingPrice(false)}>Annuler</button>
              <button className="gm-relink-save" disabled={busy || priceInput === ''} onClick={savePriceEdit}>Enregistrer</button>
            </div>
          </div>
        )}

        <div className="gm-source">
          {!relinking ? (
            <div className="gm-source-row">
              <span>
                {ing.source_product_name ? (
                  <>{ing.price_source && ing.price_source !== 'invoice' ? 'Produit facturé, pour comparer : ' : 'Lié à : '}<b>{ing.source_product_name}</b>{ing.source_product_supplier ? <> · {ing.source_product_supplier}</> : null}
                    {ing.source_invoice_odoo_id && (
                      <> · <button type="button" className="gm-invoice-link"
                        onClick={(e) => { e.stopPropagation(); openInvoicePdf(ing.source_invoice_odoo_id); }}>
                        📄 voir la facture{ing.source_invoice_number ? ` ${ing.source_invoice_number}` : ''}</button></>
                    )}</>
                ) : (
                  <>Pas encore lié à un produit facturé.</>
                )}
              </span>
              <button className="gm-relink-btn" onClick={() => { setRelinking(true); setQ(ing.name); }}>
                {ing.source_product_name ? 'Changer' : 'Lier'}
              </button>
            </div>
          ) : (
            <div className="gm-relink">
              {pendingProduct ? (
                <div className="gm-confirm">
                  <div className="gm-confirm-head">
                    <span className="gm-confirm-ico">{guessEmoji(pendingProduct.name)}</span>
                    <div className="gm-confirm-main">
                      <div className="gm-confirm-name">{pendingProduct.name}</div>
                      <div className="gm-confirm-sub">{pendingProduct.supplier_name || '—'}{pendingProduct.last_invoice_date ? ` · ${relativeFr(pendingProduct.last_invoice_date)}` : ''}</div>
                    </div>
                    <div className="gm-confirm-price">{fmtEur(pendingProduct.unit_price)}<small> /{pendingProduct.unit}</small></div>
                  </div>
                  {pendingProduct.last_invoice_odoo_id ? (
                    <button type="button" className="gm-invoice-link gm-confirm-invoice" onClick={() => openInvoicePdf(pendingProduct.last_invoice_odoo_id)}>
                      📄 voir la facture{pendingProduct.last_invoice_number ? ` ${pendingProduct.last_invoice_number}` : ''} pour vérifier
                    </button>
                  ) : (
                    <div className="gm-confirm-noinv">Pas de PDF de facture pour ce produit.</div>
                  )}
                  <div className="gm-pe-cap" style={{ marginTop: 14 }}>Unité &amp; prix par unité</div>
                  <div className="gm-pe-conv">
                    <input className="gm-input" type="number" step="0.01" value={priceInput} onChange={(e) => setPriceInput(e.target.value)} />
                    <span className="gm-pe-x">€ pour</span>
                    <input className="gm-input gm-pe-qty" type="number" step="0.01" value={qtyInput} onChange={(e) => setQtyInput(e.target.value)} />
                  </div>
                  <div className="gm-units" style={{ marginTop: 8 }}>
                    {['kg', 'g', 'l', 'cl', 'ml', 'unit'].map((u) => (
                      <button key={u} className={`gm-chip ${u === mUnit ? 'on' : ''}`} onClick={() => setMUnit(u)}>{UNIT_LABEL[u] || u}</button>
                    ))}
                  </div>
                  {peResult != null && <div className="gm-pe-result">= {fmtEur(peResult)} / {mUnit}</div>}
                  <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                    <button className="gm-btn-ghost" disabled={busy} onClick={() => setPendingProduct(null)}>← retour</button>
                    <button className="gm-relink-save" disabled={busy || priceInput === ''} onClick={() => relinkTo(pendingProduct)}>{busy ? 'Un instant…' : 'Lier & enregistrer'}</button>
                  </div>
                </div>
              ) : !manualMode ? (
                <>
                  <input className="gm-input" autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher la bonne ligne de facture…" />
                  <div className="gm-lookup">
                    <div className="hint">Choisir le produit facturé à lier</div>
                    {results.map((p) => (
                      <button key={p.id} className="gm-res" onClick={() => { setPendingProduct(p); setPriceInput(String(p.unit_price ?? '')); setQtyInput('1'); setMUnit(p.unit || ing.unit); }}>
                        <span className="ri">{guessEmoji(p.name)}</span>
                        <div className="rmain"><span className="rn">{p.name.length > 52 ? p.name.slice(0, 52) + '…' : p.name}
                          <span className="rsub">{p.supplier_name || '—'}{p.last_invoice_date ? ` · ${new Date(p.last_invoice_date).toLocaleDateString('fr-FR')}` : ''}</span></span>
                          <span className="leader" /><span className="rp">{fmtEur(p.unit_price)}/{p.unit}</span></div>
                      </button>
                    ))}
                    {q.trim().length >= 2 && results.length === 0 && <div className="gm-lookup-empty">Rien trouvé</div>}
                    <button className="gm-res" onClick={() => { setManualMode(true); setPriceInput(ing.unit_price ? String(ing.unit_price) : ''); }}>
                      <span className="ri">✏️</span>
                      <div className="rmain"><span className="rn">Saisir un prix moi-même<span className="rsub">sans lier de produit</span></span>
                        <span className="leader" /><span className="rp" style={{ color: 'var(--faint)' }}>€ —</span></div>
                    </button>
                  </div>
                  <button className="gm-btn-ghost" style={{ marginTop: 8 }} onClick={() => { setRelinking(false); setManualMode(false); }}>Annuler</button>
                </>
              ) : (
                <>
                  <input className="gm-input" type="number" step="0.01" autoFocus placeholder="Prix en €" value={priceInput} onChange={(e) => setPriceInput(e.target.value)} />
                  <div className="gm-units" style={{ marginTop: 8 }}>
                    {['kg', 'g', 'l', 'unit'].map((u) => (
                      <button key={u} className={`gm-chip ${u === mUnit ? 'on' : ''}`} onClick={() => setMUnit(u)}>{UNIT_LABEL[u] || u}</button>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                    <button className="gm-btn-ghost" onClick={() => setManualMode(false)}>← retour</button>
                    <button className="gm-relink-save" disabled={busy || priceInput === ''} onClick={saveManual}>Enregistrer le prix</button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {ing.price_has_drifted && !recentOpen && (
          <div className="gm-nudge">
            <span className="ne">📈</span>
            <div className="nt"><b>Le prix de référence a changé.</b> Une facture plus récente est à <b>{fmtEur(ing.latest_catalog_price)}/{ing.unit}</b>. À vous de vérifier et d’ajuster.</div>
            <button disabled={busy} onClick={loadRecent}>Vérifier</button>
          </div>
        )}

        {ing.source_product_id && !recentOpen && (
          <button type="button" className="gm-checkupd" onClick={loadRecent}>Vérifier les prix récents</button>
        )}

        {recentOpen && (
          <div className="gm-recent">
            <div className="gm-recent-head">
              <span>Factures récentes. Pour référence : le prix ne change pas tout seul.</span>
              <button className="gm-x" onClick={() => setRecentOpen(false)}>✕</button>
            </div>
            {recentLines === null ? (
              <div className="gm-recent-empty">Chargement…</div>
            ) : recentLines.length === 0 ? (
              <div className="gm-recent-empty">Aucune facture récente trouvée.</div>
            ) : recentLines.map((ln, i) => (
              <div className="gm-recent-row" key={i}>
                <div className="gm-recent-main">
                  <span className="gm-recent-date">{ln.date ? new Date(ln.date).toLocaleDateString('fr-FR') : '—'}</span>
                  <span className="gm-recent-qty">{ln.quantity} {ln.uom} · {fmtEur(ln.price_subtotal)}</span>
                  {ln.invoice_odoo_id && (
                    <button type="button" className="gm-invoice-link" onClick={() => openInvoicePdf(ln.invoice_odoo_id)}>📄</button>
                  )}
                </div>
                <button type="button" className="gm-relink-btn" onClick={() => useRecentLine(ln)}>Utiliser</button>
              </div>
            ))}
          </div>
        )}

        <div className="gm-hist">
          <div className="cap">Historique du prix</div>
          {history.length === 0 ? (
            <div style={{ fontSize: 12, color: 'var(--faint)' }}>Pas encore d’historique.</div>
          ) : (
            <>
              <div className="gm-spark">
                <div className="now">{fmtEur(ing.unit_price)}</div>
                {spark && <svg width="148" height="50" viewBox="0 0 148 50" fill="none"><polyline points={spark} stroke="#114817" strokeWidth="3" /></svg>}
              </div>
              <div className="gm-hist-list">
                {history.map((h) => (
                  <div className="h" key={h.id}>
                    <span>{new Date(h.effective_date).toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' })}
                      {h.source_invoice_odoo_id && (
                        <button type="button" className="gm-invoice-link" title={`Facture ${h.source_invoice_number || ''}`}
                          onClick={() => openInvoicePdf(h.source_invoice_odoo_id)}> 📄</button>
                      )}</span>
                    <span className="leader" /><b>{fmtEur(h.price)}</b>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="gm-detail-foot">
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {ing.is_active
              ? <button className="gm-btn-ghost danger" disabled={busy} onClick={() => setActive(false)}>Archiver</button>
              : <button className="gm-btn-ghost" disabled={busy} onClick={() => setActive(true)}>Réactiver</button>}
            <button className={`gm-btn-ghost ${ing.ready_to_sell ? 'on' : ''}`} disabled={busy} onClick={toggleRts}
              title="Article acheté revendu tel quel (ex. canette UMÀ Cola)">
              {ing.ready_to_sell ? '🛒 prêt-à-vendre ✓' : 'Marquer prêt-à-vendre'}
            </button>
          </div>
          <button className="gm-btn-ghost" onClick={onClose}>Fermer</button>
        </div>
      </div>
    </div>
  );
};

/* ---------- page ---------- */
const Ingredients = () => {
  const [ingredients, setIngredients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [rtsOnly, setRtsOnly] = useState(false);
  const [adding, setAdding] = useState(false);
  const [detail, setDetail] = useState(null);

  const fetchList = async () => {
    try {
      const { data } = await axiosInstance.get(showArchived ? 'ingredients/?preparation=false' : 'ingredients/?active=true&preparation=false');
      setIngredients(Array.isArray(data) ? data : data?.results ?? []);
    } catch (e) { console.error('fetch pantry failed', e); setIngredients([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { document.title = 'Ingrédients | Costy'; }, []);
  useEffect(() => { setLoading(true); fetchList(); }, [showArchived]); // eslint-disable-line

  const shown = useMemo(() => {
    const q = searchKey(search.trim());
    let list = q ? ingredients.filter((i) => searchKey(i.name).includes(q)) : ingredients;
    if (rtsOnly) list = list.filter((i) => i.ready_to_sell);
    return list;
  }, [ingredients, search, rtsOnly]);

  const refreshAfterChange = () => { setAdding(false); setDetail(null); fetchList(); };

  return (
    <Layout>
      <div className="gm space-y-6">
        <PageHeader title="Ingrédients" subtitle={loading ? null : `${shown.length} ingrédients`}>
          <button type="button" className="btn-primary" onClick={() => setAdding(true)}>
            <Plus className="h-4 w-4" />
            Nouvel ingrédient
          </button>
        </PageHeader>
        <div className="gm-tools">
          <div className="gm-search">
            <Search size={16} strokeWidth={2.4} />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher un ingrédient…" aria-label="Rechercher un ingrédient" />
          </div>
          <button type="button" aria-pressed={rtsOnly} className="chip" onClick={() => setRtsOnly((s) => !s)}>Prêt-à-vendre</button>
          <button type="button" aria-pressed={showArchived} className="chip" onClick={() => setShowArchived((s) => !s)}>Inclure les archivés</button>
        </div>

        {loading ? (
          <div className="gm-loading">Chargement…</div>
        ) : (
          <div className="gm-list">
            {shown.map((ing) => {
              const f = freshness(ing);
              return (
                <button key={ing.id} className={`gm-row ${ing.is_active ? '' : 'archived'}`} onClick={() => setDetail(ing)}>
                  <IngredientIcon ingredient={ing} />
                  <span className="gm-row-main">
                    <span className="nm">{ing.name}{ing.ready_to_sell && <span className="gm-rts">prêt-à-vendre</span>}</span>
                    <span className="gm-fresh">
                      {ing.is_active
                        ? <><span className={`gm-dot ${f.cls}`} />{f.label}</>
                        : <span style={{ color: 'var(--faint)' }}>archivé</span>}
                    </span>
                  </span>
                  {ing.price_has_drifted && <span className="gm-stamp">prix ↑</span>}
                  <span className="gm-row-price">{fmtEur(ing.unit_price)}<span className="per"> /{UNIT_LABEL[ing.unit] || ing.unit}</span></span>
                </button>
              );
            })}
            {shown.length === 0 && (
              <div className="gm-empty">Aucun ingrédient.</div>
            )}
          </div>
        )}

        {adding && <AddSheet onClose={() => setAdding(false)} onCreated={refreshAfterChange} />}
        {detail && <DetailSheet ingredient={detail} onClose={() => setDetail(null)} onChanged={refreshAfterChange} />}
      </div>
    </Layout>
  );
};

export default Ingredients;
